import React from 'react';
import { Section } from '../design-system';
import { Hero } from '../hero';

export interface PolicySection {
  title: string;
  icon: React.ReactElement;
  content: string;
  bullets?: string[];
}

interface PolicyPageLayoutProps {
  title: string;
  description: string;
  effectiveDate: string;
  intro: string;
  sections: PolicySection[];
}

const PolicyPageLayout: React.FC<PolicyPageLayoutProps> = ({
  title,
  description,
  effectiveDate,
  intro,
  sections,
}) => (
  <>
    <Hero
      narrow
      eyebrow="legal · policy"
      title={title}
      lede={description}
      metrics={[{ value: effectiveDate, label: 'effective' }]}
    />

    <Section tight>
      <div style={{ maxWidth: 820, margin: '0 auto' }}>
        <p
          style={{
            color: 'var(--ink-dim)',
            fontSize: 16,
            lineHeight: 1.7,
            marginBottom: 32,
            paddingBottom: 24,
            borderBottom: '1px dashed var(--line-strong)',
          }}
        >
          {intro}
        </p>

        <div style={{ display: 'grid', gap: 14 }}>
          {sections.map((section, index) => (
            <div
              key={`${section.title}-${index}`}
              style={{
                padding: 24,
                border: '1px solid var(--line)',
                borderRadius: 'var(--r)',
                background: 'var(--bg-elev)',
                transition: 'border-color 0.2s var(--ease)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  marginBottom: 12,
                }}
              >
                <span
                  style={{
                    display: 'inline-grid',
                    placeItems: 'center',
                    width: 44,
                    height: 44,
                    borderRadius: 'var(--r-sm)',
                    border: '1px solid var(--line-strong)',
                    background: 'color-mix(in oklab, var(--accent) 10%, var(--bg-elev-2))',
                    color: 'var(--accent)',
                  }}
                >
                  {section.icon}
                </span>
                <h3
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 18,
                    fontWeight: 600,
                    letterSpacing: '-0.015em',
                    color: 'var(--ink)',
                  }}
                >
                  {section.title}
                </h3>
              </div>
              <p style={{ color: 'var(--ink-dim)', fontSize: 14.5, lineHeight: 1.65 }}>
                {section.content}
              </p>
              {section.bullets && section.bullets.length > 0 && (
                <ul
                  style={{
                    listStyle: 'none',
                    marginTop: 14,
                    paddingLeft: 0,
                    color: 'var(--ink-dim)',
                    fontSize: 14,
                    lineHeight: 1.65,
                  }}
                >
                  {section.bullets.map((bullet, bulletIndex) => (
                    <li
                      key={`${section.title}-bullet-${bulletIndex}`}
                      style={{
                        position: 'relative',
                        paddingLeft: 20,
                        marginBottom: 6,
                      }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 1,
                          color: 'var(--accent)',
                          fontFamily: 'var(--font-mono)',
                          fontSize: 12,
                        }}
                      >
                        ▸
                      </span>
                      {bullet}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>
    </Section>
  </>
);

export default PolicyPageLayout;
